import React, { createContext, useContext, ReactNode } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { upsertStaffUserOnLogin, logAuditEvent } from '../staffPlatformService';
import { canManageSettings, canManageUsers, StaffRole, StaffUser } from '../types/staffPlatform';

interface AuthContextType {
  isAuthenticated: boolean;
  user: any;
  staffProfile: StaffUser | null;
  staffRole: StaffRole | null;
  canManageUsers: boolean;
  canManageSettings: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  staffLoading: boolean;
  refreshStaffProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [loading, setLoading] = React.useState(false);
  const [staffLoading, setStaffLoading] = React.useState(false);
  const [staffProfile, setStaffProfile] = React.useState<StaffUser | null>(null);

  const user = accounts.length > 0 ? accounts[0] : null;
  const staffRole = staffProfile?.role ?? null;

  const syncStaffProfile = async (account: typeof accounts[0]) => {
    if (!account) return;

    setStaffLoading(true);
    try {
      const email = account.username || account.idTokenClaims?.preferred_username as string || '';
      const name = account.name || email;
      const result = await upsertStaffUserOnLogin(email, name);

      if (result.success && result.user) {
        setStaffProfile(result.user);
        await logAuditEvent({
          action: 'staff.login',
          actorEmail: email,
          actorName: name,
          targetType: 'staff',
          targetId: result.user.id,
          targetLabel: name,
          details: 'Staff signed in to the platform',
        });
      }
    } catch (error) {
      console.error('Failed to sync staff profile:', error);
    } finally {
      setStaffLoading(false);
    }
  };

  React.useEffect(() => {
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  React.useEffect(() => {
    if (isAuthenticated && accounts[0]) {
      syncStaffProfile(accounts[0]);
    } else {
      setStaffProfile(null);
    }
  }, [isAuthenticated, accounts]);

  const refreshStaffProfile = async () => {
    if (accounts[0]) {
      await syncStaffProfile(accounts[0]);
    }
  };

  const login = async () => {
    setLoading(true);
    try {
      const response = await instance.loginPopup(loginRequest);

      if (response.account) {
        instance.setActiveAccount(response.account);
      }
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      if (user) {
        await logAuditEvent({
          action: 'staff.logout',
          actorEmail: user.username || '',
          actorName: user.name || user.username || 'Staff',
          targetType: 'staff',
          targetId: user.username || '',
          targetLabel: user.name || user.username || 'Staff',
          details: 'Staff signed out of the platform',
        });
      }

      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
      setStaffProfile(null);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    staffProfile,
    staffRole,
    canManageUsers: canManageUsers(staffRole),
    canManageSettings: canManageSettings(staffRole),
    login,
    logout,
    loading,
    staffLoading,
    refreshStaffProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
